import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { HashtagNode } from '@lexical/hashtag';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { OverflowNode } from '@lexical/overflow';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellHeaderStates, TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { Klass, LexicalEditor, LexicalNode, TabNode } from 'lexical';
import { KeyValueMap } from '../KeyValueMap';
import { ActionType } from '../components/ActionType';
import { AttachmentReferenceNode } from './AttachmentReferenceNode';
import { ExtendedTextNode } from './ExtendedTextNode';
import { LayoutContainerNode } from './LayoutContainerNode';
import { LayoutItemNode } from './LayoutItemNode';
import { MentionsNode } from './MentionsNode';
import { RichTextNode } from './RichTextNode';

export const URL_MATCHER =
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

export const EMPTY_EDITOR_STATE =
    '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

export const REGEX_FOR_HTML = /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/;

// Lexical nodes are the building blocks of the Lexical editor’s document.
// Each node represents something like text, a paragraph, a heading, or an image, and together they form the editor state.

export const AVAILABLE_NODES: Array<Klass<LexicalNode>> = [
    AutoLinkNode,
    CodeHighlightNode,
    CodeNode,
    HashtagNode,
    HeadingNode,
    HorizontalRuleNode,
    LinkNode,
    ListItemNode,
    ListNode,
    MarkNode,
    AttachmentReferenceNode,
    MentionsNode,
    OverflowNode,
    QuoteNode,
    TableCellNode,
    TableNode,
    TableRowNode,
    TabNode,
    LayoutContainerNode,
    LayoutItemNode,
    RichTextNode,
    ExtendedTextNode
];

export const MATCHERS = [
    (text) => {
        const match = URL_MATCHER.exec(text);
        if (match === null) {
            return null;
        }
        const fullMatch = match[0];
        return {
            index: match.index,
            length: fullMatch.length,
            text: fullMatch,
            url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
            attributes: { rel: 'noopener', target: '_blank' } // Optional link attributes
        };
    }
];

export const findFirstTextLine = (data) => {
    const queue = [data];

    while (queue.length > 0) {
        const currentNode = queue.shift();

        if (currentNode.type === 'text') {
            return currentNode.text;
        }

        if (currentNode.children) {
            queue.push(...currentNode.children);
        }
    }
};

export const getTableActions = (
    cellNode: TableCellNode | null,
    headerState: number
): KeyValueMap<string, ActionType> => {
    if (!cellNode) return {};
    return {
        deleteRow: { label: 'lexical.tablecell.menu.delete.row' },
        deleteColumn: { label: 'lexical.tablecell.menu.delete.column' },
        deleteTable: { label: 'lexical.tablecell.menu.delete.table', divider: true },

        insertColumnLeft: { label: 'lexical.tablecell.menu.insert.column.left' },
        insertColumnRight: { label: 'lexical.tablecell.menu.insert.column.right', divider: true },

        insertRowAbove: { label: 'lexical.tablecell.menu.insert.row.above' },
        insertRowBelow: { label: 'lexical.tablecell.menu.insert.row.below', divider: true },

        toggleRowHeader: {
            label:
                headerState === TableCellHeaderStates.ROW || headerState === TableCellHeaderStates.BOTH
                    ? 'lexical.tablecell.menu.remove.row.header'
                    : 'lexical.tablecell.menu.add.row.header'
        },
        toggleColumnHeader: {
            label:
                headerState === TableCellHeaderStates.COLUMN || headerState === TableCellHeaderStates.BOTH
                    ? 'lexical.tablecell.menu.remove.column.header'
                    : 'lexical.tablecell.menu.add.column.header'
        }
    };
};

export const getDropdownCoords = (
    cellNode: TableCellNode | null,
    anchorElem: HTMLElement,
    buttonSize: number,
    editor: LexicalEditor
) => {
    if (!cellNode || !anchorElem) return { x: 0, y: 0 };
    const cellDom = editor.getElementByKey(cellNode.getKey()) as HTMLElement | null;
    if (!cellDom) return { x: 0, y: 0 };
    const rect = cellDom.getBoundingClientRect();
    const anchorRect = anchorElem.getBoundingClientRect();
    return {
        x: Math.max(0, rect.right - anchorRect.left - buttonSize),
        y: Math.max(0, rect.top - anchorRect.top)
    };
};

export const getNewHeaderState = (currentState: number, type: 'row' | 'column'): number => {
    if (type === 'row') {
        return currentState === TableCellHeaderStates.ROW
            ? TableCellHeaderStates.NO_STATUS
            : currentState === TableCellHeaderStates.BOTH
              ? TableCellHeaderStates.COLUMN
              : TableCellHeaderStates.ROW;
    } else {
        return currentState === TableCellHeaderStates.COLUMN
            ? TableCellHeaderStates.NO_STATUS
            : currentState === TableCellHeaderStates.BOTH
              ? TableCellHeaderStates.ROW
              : TableCellHeaderStates.COLUMN;
    }
};
